export interface AttentionThresholds {
  gatePassRateMin: number;
  inactiveDays: number;
}

const DEFAULT_THRESHOLDS: AttentionThresholds = {
  gatePassRateMin: 90,
  inactiveDays: 14,
};

export type AttentionReason = "lowGates" | "cycleRising" | "inactive";

export interface ProjectHealth {
  slug: string;
  name: string;
  gatePassRate: number | null;
  cycleDelta: number | null;
  lastActivityDate: string | null;
  needsAttention: boolean;
  reasons: AttentionReason[];
}

export interface PortfolioResult {
  projects: ProjectHealth[];
  attentionCount: number;
}

export interface ProjectInput {
  slug: string;
  name: string;
  gatePassRate: number | null;
  cycleDelta: number | null;
  lastActivityDate: string | null;
}

export function evaluateAttention(
  project: ProjectInput,
  todayIso: string,
  thresholds: AttentionThresholds = DEFAULT_THRESHOLDS,
): { needsAttention: boolean; reasons: AttentionReason[] } {
  const reasons: AttentionReason[] = [];

  if (
    project.gatePassRate !== null &&
    project.gatePassRate < thresholds.gatePassRateMin
  ) {
    reasons.push("lowGates");
  }

  if (project.cycleDelta !== null && project.cycleDelta > 0) {
    reasons.push("cycleRising");
  }

  if (project.lastActivityDate !== null) {
    const diffMs =
      new Date(todayIso).getTime() -
      new Date(project.lastActivityDate).getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays >= thresholds.inactiveDays) {
      reasons.push("inactive");
    }
  }

  return { needsAttention: reasons.length > 0, reasons };
}

export function buildPortfolio(
  projects: ProjectInput[],
  todayIso: string,
  thresholds?: AttentionThresholds,
): PortfolioResult {
  let attentionCount = 0;
  const result: ProjectHealth[] = projects.map((p) => {
    const { needsAttention, reasons } = evaluateAttention(
      p,
      todayIso,
      thresholds,
    );
    if (needsAttention) attentionCount++;
    return { ...p, needsAttention, reasons };
  });

  return { projects: result, attentionCount };
}
